import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { ReactSVG } from 'react-svg'

import { useCartInfoStore, useCartStore } from '@entities/Cart'

import { useAuth, useAuthFlow } from '@features/auth'
import useClearCartModal from '@features/cart/hooks/useClearCart'
import { useAddressStore, useDelivery } from '@features/shipping-methods'

import useDeliveryModal from '@widgets/modal-delivery/ui/useDeliveryModal'

import { CART_FEATURES, URL_CART, URL_PAGE_CATALOG } from '@shared/contants'
import { useZonesFromAddress } from '@shared/hooks/useZonesFromAddress'
import { computeCartTotalsBreakdown } from '@shared/lib/cartTotals'
import { cn } from '@shared/lib/cn'
import { groupProductsByCategory } from '@shared/lib/groupProductsByCategory'
import Button from '@shared/ui/button'
import Wrapper from '@shared/ui/wrapper'
import { formatPrice } from '@shared/uttils'
import { formatGramsToHumanWeight } from '@shared/uttils/formatGramsToHumanWeight'
import getNoun from '@shared/uttils/getNoun'

import CartListPreview from './list'

import styles from './styles.module.scss'

function SkeletonLoading () {
  return (
    <SkeletonTheme>
      <Skeleton
        count={3}
        width={'100%'}
        height={80}
        style={{
          borderRadius: 6,
          marginBottom: 20,
        }}
      />
    </SkeletonTheme>
  )
}

export default function CartPreview () {
  const { t: tca } = useTranslation('cart')
  const { t: tc } = useTranslation('common')
  const { t: ta } = useTranslation('auth')
  const { t: tp } = useTranslation('product')
  const { t: to } = useTranslation('order')
  const { user } = useAuth()
  const { getCart, isSyncing } = useCartStore()
  const cartTotal = useCartInfoStore((state) => state.cartInfo)
  const loading = useCartInfoStore((state) => state.cartInfoLoading)

  const cart = getCart()

  const { openClearCartModal } = useClearCartModal()
  const {
    minOrderCost,
    isPickup,
    freeSlotDeliveryCostFrom,
    deliveryCost,
    loading: zonesLoading,
  } = useZonesFromAddress()
  const { deliveryType, maxSlotWeight, maxExpressWeight } = useDelivery()
  const authFlow = useAuthFlow()
  const { openDeliveryModal } = useDeliveryModal()
  const [ showDeliveryList, setShowDeliveryList ] = useState(false)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const [ hasScroll, setHasScroll ] = useState(false)
  const { selectedAddress } = useAddressStore()

  const total = cartTotal?.totals?.total as number
  const cartWeight = useMemo(() => Number(cartTotal?.totalWeight ?? 0), [ cartTotal?.totalWeight ])
  const weightLimit = useMemo(() => {
    return deliveryType === 'express' ? maxExpressWeight : maxSlotWeight
  }, [ deliveryType, maxExpressWeight, maxSlotWeight ])
  const weightExceeded = useMemo(() => {
    if (!weightLimit || weightLimit <= 0) return false
    return cartWeight > weightLimit
  }, [ cartWeight, weightLimit ])

  const {
    deliveryTotal,
    deliverySale,
    orderTotal,
    couponSale,
    bonusSale,
    loyaltyDiscount,
    hasSalesBreakdown,
  } = computeCartTotalsBreakdown(cartTotal, isPickup())

  const hasItems = useMemo(() => {
    // Учитываем только товары, которые не раскуплены
    return !!cart?.items.filter(item => !item.isSoldOut && item.amount > 0).length
  }, [ cart ])

  const totalItems = useMemo(() => {
    // Считаем только товары, которые не раскуплены
    return cart?.items.filter(item => !item.isSoldOut && item.amount > 0).length ?? 0
  }, [ cart ])

  const isLoading = useMemo(() => {
    // Первичная загрузка: показываем скелетон, пока нет cartTotal
    // После первой загрузки не мерцаем при фоновом refetch
    return loading && !cartTotal
  }, [ loading, cartTotal ])

  const productsData = useMemo(() => {
    return cartTotal?.products?.map((p) => p?.info).filter(Boolean) ?? []
  }, [ cartTotal ])

  // Разделяем товары на доступные и раскупленные
  const { inStockProducts, outOfStockProducts } = useMemo(() => {
    const inStock: typeof productsData = []
    const outOfStock: typeof productsData = []
    
    productsData.forEach((product) => {
      if (!product) return
      const cartItem = cart?.items.find((i) => String(i.product_id) === String(product.id))
      if (cartItem?.isSoldOut) {
        outOfStock.push(product)
      }
      else {
        inStock.push(product)
      }
    })
    
    return { inStockProducts: inStock, outOfStockProducts: outOfStock }
  }, [ productsData, cart?.items ])

  // Группируем товары в наличии по категориям
  const groupedInStockProducts = useMemo(() => {
    return groupProductsByCategory(inStockProducts)
  }, [ inStockProducts ])

  useEffect(() => {
    if (scrollAreaRef.current) {
      const hasVerticalScroll =
        scrollAreaRef.current.scrollHeight > scrollAreaRef.current.clientHeight
      setHasScroll(hasVerticalScroll)
    }
  }, [ productsData ])

  return (
    <Wrapper
      className={cn(styles.root, {
        [styles.products]: hasItems,
        [styles.empty]: !hasItems,
      })}
    >
      {!hasItems && (
        <div className={styles.root__top}>
          {isLoading ? (
            <SkeletonLoading />
          ) : !selectedAddress ? (
            <h2 className={styles.root__title}>{tca('preview.title')}</h2>
          ) : (
            <>
              <h2 className={styles.root__title}>
                {tca('preview.titleSelectedAddress')}
              </h2>
              <p className={styles.root__desc}>
                {tca('preview.useSearchOrCatalog')}
              </p>
              <Button size="large" to={URL_PAGE_CATALOG}>
                {tc('button.catalog')}
              </Button>
            </>
          )}
          {!isLoading && !selectedAddress && (
            <>
              <p className={styles.root__desc}>{tca('preview.desc')}</p>
              <Button size="large" onClick={() => openDeliveryModal()}>
                {tc('address.choose')}
              </Button>
            </>
          )}
        </div>
      )}

      {hasItems && (
        <>
          {!isLoading && (
            <div className={styles.root__top}>
              <span className={styles.root__count}>
                {totalItems}{' '}
                {getNoun(
                  totalItems,
                  tp('product.1'),
                  tp('product.2'),
                  tp('product.3'),
                )}
              </span>
              <span className={styles.root__head}>
                {!!cartTotal?.totalWeight && (
                  <p
                    className={cn(styles.root__weight, {
                      [styles.root__weightDanger]: weightExceeded,
                    })}
                  >
                    ~{' '}
                    {formatGramsToHumanWeight({
                      weight: Number(cartTotal?.totalWeight),
                      t: tc,
                    })}
                    {weightLimit > 0 && (
                      <>
                        {' '}
                        / {tca('preview.weight_limit', {
                          maxWeight: formatGramsToHumanWeight({
                            weight: weightLimit,
                            t: tc,
                          }),
                        })}
                      </>
                    )}
                  </p>
                )}
                <button
                  className={cn('reset-button', styles.root__clear)}
                  onClick={openClearCartModal}
                >
                  {tca('preview.clear_button')}
                </button>
              </span>
            </div>
          )}

          <div
            ref={scrollAreaRef}
            className={cn(styles.scrollArea, { [styles.hasScroll]: hasScroll })}
          >
            {!isLoading ? (
              <>
                {outOfStockProducts.length > 0 && (
                  <div className={styles.soldOutSection}>
                    <div className={styles.soldOutSection__header}>
                      {tc('sold_out')}
                    </div>
                    <CartListPreview data={outOfStockProducts} />
                  </div>
                )}
                {groupedInStockProducts.map((group) => (
                  <div key={group.categoryName} className={styles.categorySection}>
                    <div className={styles.categorySection__header}>
                      {group.categoryName}
                    </div>
                    <CartListPreview data={group.products} />
                  </div>
                ))}
              </>
            ) : (
              <SkeletonLoading />
            )}
          </div>
        </>
      )}

      {!hasItems && !isLoading && (
        <div className={styles.root__bottom}>
          <div className={styles.root__list}>
            {CART_FEATURES.map((item) => (
              <div key={item.key} className={styles.root__item}>
                <ReactSVG
                  className={styles.root__icon}
                  src={`/icons/cart/features/${item.key}.svg`}
                  wrapper="span"
                />
                {tca(`preview.features.${item.key}`)}
              </div>
            ))}
          </div>

          {!user?.first_name && (
            <Button size="large" theme="secondary" onClick={authFlow}>
              {ta('button.auth')}
            </Button>
          )}
        </div>
      )}

      {hasItems && cartTotal && (
        <div className={styles.bottomGroup}>
          <div className={styles.root__total}>
            {/* 1. Товары - базовая стоимость */}
            {(cartTotal?.totals?.baseTotal ?? 0) > 0 && (
              <div className={styles['root__total-row']}>
                <h3 className={styles['root__total-title']}>
                  {tca('total.products')}:{' '}
                </h3>
                <span className={styles['root__total-value']}>
                  {formatPrice(cartTotal?.totals?.baseTotal ?? 0)}
                </span>
              </div>
            )}

            {/* 2. Скидка на товары (желтая цена) */}
            {(cartTotal?.totals?.baseSale ?? 0) > 0 && (
              <div className={styles['root__total-row']}>
                <h3 className={styles['root__total-title']}>
                  {tca('total.discountOnProducts')}:{' '}
                </h3>
                <span className={styles['root__total-sale']}>
                  -{formatPrice(cartTotal?.totals?.baseSale ?? 0)}
                </span>
              </div>
            )}

            {/* 3. Промокод/бонусы/лояльность */}
            {hasSalesBreakdown ? (
              <>
                {bonusSale > 0 && (
                  <div className={styles['root__total-row']}>
                    <h3 className={styles['root__total-title']}>
                      {tca('total.bonusCharge')}:{' '}
                    </h3>
                    <span className={styles['root__total-sale']}>
                      -{formatPrice(bonusSale)}
                    </span>
                  </div>
                )}

                {couponSale > 0 && (
                  <div className={styles['root__total-row']}>
                    <h3 className={styles['root__total-title']}>
                      {tca('total.couponUsed')}:{' '}
                    </h3>
                    <span className={styles['root__total-sale']}>
                      -{formatPrice(couponSale)}
                    </span>
                  </div>
                )}

                {loyaltyDiscount > 0 && (
                  <div className={styles['root__total-row']}>
                    <h3 className={styles['root__total-title']}>
                      {tca('total.loyaltyDiscount')}:{' '}
                    </h3>
                    <span className={styles['root__total-sale']}>
                      -{formatPrice(loyaltyDiscount)}
                    </span>
                  </div>
                )}
              </>
            ) : (cartTotal?.totals?.sale ?? 0) > 0 && (
              <div className={styles['root__total-row']}>
                <h3 className={styles['root__total-title']}>
                  {cartTotal?.promocode 
                    ? tca('total.promocode')
                    : tca('total.discount')}:{' '}
                </h3>
                <span className={styles['root__total-sale']}>
                  -{formatPrice(cartTotal?.totals?.sale ?? 0)}
                </span>
              </div>
            )}

            {/* 4. Доставка */}
            {!isPickup() && deliveryTotal > 0 && (
              <div className={styles['root__total-row']}>
                <h3 className={styles['root__total-title']}>
                  {tca('total.delivery')}:{' '}
                </h3>
                <span className={styles['root__total-value']}>
                  {formatPrice(deliveryTotal - deliverySale)}
                </span>
              </div>
            )}
            {!isPickup() && deliverySale > 0 && (
              <div className={styles['root__total-row']}>
                <h3 className={styles['root__total-title']}>
                  {tca('total.discount')}:{' '}
                </h3>
                <span className={styles['root__total-sale']}>
                  -{formatPrice(deliverySale)}
                </span>
              </div>
            )}

            {/* 5. Итого */}
            <div className={styles['root__total-row']}>
              <h3 className={styles['root__total-title']}>
                {tca('total.total')}:{' '}
              </h3>
              <b className={styles['root__total-price']}>
                {formatPrice(orderTotal)}
              </b>
            </div>
          </div>
          <div className={styles['root__buttons']}>
            {isLoading || zonesLoading ? (
              <SkeletonTheme>
                <Skeleton
                  count={1}
                  width="100%"
                  height={56}
                  style={{
                    borderRadius: 12,
                  }}
                />
              </SkeletonTheme>
            ) : !isPickup() ? (
              weightExceeded ? (
                <Button disabled size="large">
                  {to('weight_exceeded', {
                    maxWeight: formatGramsToHumanWeight({
                      weight: weightLimit || 0,
                      t: tc,
                    }),
                  })}
                </Button>
              ) : total < minOrderCost ? (
                <Button disabled size="large">
                  {tca('preview.min_order_message', {
                    minOrderCost: formatPrice(minOrderCost),
                    amount: formatPrice(minOrderCost - total),
                  })}
                </Button>
              ) : (
                <Button size="large" to={URL_CART} disabled={isSyncing}>
                  {tca('preview.go_to_cart')}
                </Button>
              )
            ) : (
              weightExceeded ? (
                <Button size="large" disabled>
                  {to('weight_exceeded', {
                    maxWeight: formatGramsToHumanWeight({
                      weight: weightLimit || 0,
                      t: tc,
                    }),
                  })}
                </Button>
              ) : (
                <Button size="large" to={URL_CART} disabled={isSyncing}>
                  {tca('preview.go_to_cart')}
                </Button>
              )
            )}
            {isLoading || zonesLoading ? (
              <SkeletonTheme>
                <Skeleton
                  count={1}
                  width="100%"
                  height={56}
                  style={{
                    borderRadius: 12,
                  }}
                />
              </SkeletonTheme>
            ) : !isPickup() && freeSlotDeliveryCostFrom > 0 ? (
              <div
                className={styles['root__delivery-cost']}
                onClick={() => setShowDeliveryList((prev) => !prev)}
              >
                <ReactSVG
                  className={styles['root__delivery-cost__icon']}
                  src="/icons/moped.svg"
                  wrapper="span"
                />
                <span className={styles['root__delivery-cost__text']}>
                  {total < freeSlotDeliveryCostFrom
                    ? tca('delivery.amount_to_free', {
                      amount: formatPrice(freeSlotDeliveryCostFrom - total),
                    })
                    : tca('delivery.free')}
                </span>
                <ReactSVG
                  className={styles.control}
                  src="/icons/arrow-right.svg"
                />
                {showDeliveryList && (
                  <div className={styles['root__delivery-list']}>
                    <p>
                      {tca('delivery.conditions.under_min', {
                        minOrderCost: formatPrice(minOrderCost),
                        deliveryCost: formatPrice(deliveryCost),
                      })}
                    </p>
                    <p>
                      {tca('delivery.conditions.over_free', {
                        freeSlotDeliveryCostFrom: formatPrice(
                          freeSlotDeliveryCostFrom,
                        ),
                      })}
                    </p>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </Wrapper>
  )
}
